import { OrderFlowRouteName } from './model.js';
import { orderFlowHeadingWithNav, getOrderFlowMaybeAsChildName } from './privateHelpers.js';

const getMaybeAsChildProps = (asChild) => (route) => ({
    ...route.params,
    asChild,
});
/**
 * For routes that exist both as a root-level route and as a child of the visit
 * overview step. The navigation behaviour is different when accessing steps
 * from the visit overview step, so we should distinguish between the two. For
 * example, there is no back button on the first step of the root flow, but
 * there is a back button leading back to the visit overview step when the sub
 * flow is accessed from the visit overview step.
 */
const getWithAsChild = (rootName, routeConfig) => [{
        ...routeConfig,
        name: getOrderFlowMaybeAsChildName(rootName, false),
        props: getMaybeAsChildProps(false),
    }, {
        ...routeConfig,
        name: getOrderFlowMaybeAsChildName(rootName, true),
        path: `visit-overview/${routeConfig.path}`,
        props: getMaybeAsChildProps(true),
    }];
// Paths below are not full paths - they are based on the parent path:
// /:partnerName?/edit-order/:draftOrderID?
const routesOrderFlowChildren = [
    ...getWithAsChild(OrderFlowRouteName.ORDER_FLOW_PDF_UPLOAD, {
        path: 'booking/:bookingID/upload/procedure/:procedureID?',
        component: () => import('../../../components/specific/orderFlow/OrderFlow/steps/substeps/OrderFlowVisitOverview/OrderFlowPDFUpload.vue.js'),
        meta: {
            authRequired: true,
            heading: orderFlowHeadingWithNav('Order'),
            whiteBackground: true,
        },
    }),
    ...getWithAsChild(OrderFlowRouteName.ORDER_FLOW_ORDER_PROVIDER, {
        path: 'booking/:bookingID/provider',
        component: () => import('../../../components/specific/orderFlow/OrderFlow/steps/substeps/OrderFlowVisitOverview/OrderFlowOrderProvider.vue.js'),
        meta: {
            authRequired: true,
            heading: orderFlowHeadingWithNav('Order'),
            whiteBackground: true,
        },
    }),
    ...getWithAsChild(OrderFlowRouteName.ORDER_FLOW_MANUAL_INPUT, {
        path: 'booking/:bookingID/manual-input/procedure/:procedureID?',
        component: () => import('../../../components/specific/orderFlow/OrderFlow/steps/substeps/OrderFlowVisitOverview/OrderFlowManualInput.vue.js'),
        meta: {
            authRequired: true,
            heading: orderFlowHeadingWithNav('Order'),
            whiteBackground: true,
        },
    }),
    ...getWithAsChild(OrderFlowRouteName.ORDER_FLOW_ORDER_MEDICAL_DETAILS, {
        path: 'booking/:bookingID/medical-details/procedure/:procedureID?',
        component: () => import('../../../components/specific/orderFlow/OrderFlow/steps/substeps/OrderFlowVisitOverview/OrderFlowMedicalDetails.vue.js'),
        meta: {
            authRequired: true,
            heading: orderFlowHeadingWithNav('Order'),
            whiteBackground: true,
        },
    }),
    ...getWithAsChild(OrderFlowRouteName.ORDER_FLOW_ADDITIONAL_INFORMATION, {
        path: 'booking/:bookingID/additional-information/procedure/:procedureID?',
        component: () => import('../../../components/specific/orderFlow/OrderFlow/steps/substeps/OrderFlowVisitOverview/OrderFlowAdditionalInformation.vue.js'),
        meta: {
            authRequired: true,
            heading: orderFlowHeadingWithNav('Additional information'),
            whiteBackground: true,
        },
    }),
    {
        path: 'bulk',
        name: OrderFlowRouteName.ORDER_FLOW_BULK_UPLOAD,
        props: true,
        component: () => import('../../../components/specific/orderFlow/OrderFlow/steps/substeps/OrderFlowVisitOverview/OrderFlowBulkOrderInput.vue.js'),
        meta: {
            authRequired: true,
            heading: orderFlowHeadingWithNav('Order'),
            whiteBackground: true,
        },
    },
    {
        path: 'fulfiller',
        name: OrderFlowRouteName.ORDER_FLOW_ORDER_FULFILLER,
        props: true,
        component: () => import('../../../components/specific/orderFlow/OrderFlow/steps/substeps/OrderFlowVisitOverview/OrderFlowOrderFulfiller.vue.js'),
        meta: {
            authRequired: true,
            heading: orderFlowHeadingWithNav('Select an option'),
            whiteBackground: true,
        },
    },
    ...getWithAsChild(OrderFlowRouteName.ORDER_FLOW_SCHEDULE, {
        path: 'schedule',
        component: () => import('../../../components/specific/orderFlow/OrderFlow/steps/substeps/OrderFlowVisitOverview/OrderFlowSchedule.vue.js'),
        meta: {
            authRequired: true,
            heading: orderFlowHeadingWithNav('Schedule'),
            whiteBackground: true,
        },
    }),
    {
        path: 'visit-overview',
        name: OrderFlowRouteName.ORDER_FLOW_VISIT_OVERVIEW,
        props: true,
        component: () => import('../../../components/specific/orderFlow/OrderFlow/steps/root/OrderFlowVisitOverview.vue.js'),
        meta: {
            authRequired: true,
            // Disable back button on visit overview as the user should use the
            // cards to make changes to the bookings. For saved draft orders and
            // re-ordering, this is the flow entry step. The previous steps will still
            // be accessible and working via the browser's back button
            heading: orderFlowHeadingWithNav('Visit', true),
            whiteBackground: true,
        },
    },
];
const routesOrderFlow = [
    {
        // Lack of draftOrderID signifies a new draft order not yet saved to the
        // database, however one will be immediately generated.
        // New order route: `/edit-order` will redirect to:
        // `/edit-order/<random new ID>/order?new=true`
        // Existing order route: `/edit-order/<existing ID>` will redirect to:
        // `/edit-order/<existing ID>/visit`
        path: '/:partnerName?/edit-order/:draftOrderID?',
        props: true,
        component: () => import('../../../components/specific/orderFlow/OrderFlow/OrderFlow.vue.js'),
        children: routesOrderFlowChildren,
        meta: {
            authRequired: true,
        },
    },
    {
        // Re order route: `/re-order/<orderID>` will redirect to:
        // `/edit-order/<random new ID>/order?new=true$orderID=<orderID>`
        // DraftOrder is being generated from the order on the cloud function: initNewDraftOrderFromOrder
        path: '/:partnerName?/re-order/:reOrderID?',
        props: true,
        component: () => import('../../../components/specific/orderFlow/OrderFlow/OrderFlow.vue.js'),
        meta: {
            authRequired: true,
        },
    },
];
/**
 * RootState must be passed as type param.
 *
 * Example: `getRoutesOrderFlow<RootState>()`
 *
 * RootState is type-checked to ensure the routes cannot be added to an app that
 * does not have the necessary vuex modules set up.
 */
const getRoutesOrderFlow = () => routesOrderFlow;

export { getRoutesOrderFlow };
